import { useMemo, useState } from "react";
import { defaultFilters, ListFilters } from "./filterScreens";
import {
  BooleanHoursOfOperation,
  BooleanSeasonality,
  EnumListType,
  EnumOwnerType,
  EnumVersionedEntityReferenceType,
  EnumWidgetType,
  ListByIdQuery,
  ListManyDocument,
  OperatorManyQuery,
  OperatorManyUseCaseEnum,
  PublicationStatusType,
  QueryOperatorManyArgs,
  useListCreateMutation,
  useListUpdateByIdMutation,
  useOperatorManyQuery,
} from "gql/generated";
import _ from "lodash";
import { createMultilingual, isLongLatValid } from "shared/utils";
import { useLoggedInAs } from "shared/hooks";
import { useToastContext } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import { useGetAllOrganizationTags } from "./useGetAllOrganizationTags";
import {
  getDefaultFiltersFromListSelections,
  getTagsTagTypeForQueryArgs,
} from "./utils";
import { InternalRefetchQueriesInclude } from "@apollo/client";

export const useBuildNewListModal = (
  listName: string,
  listType: EnumListType,
  defaultList?: ListByIdQuery["listById"],
  widgetName?: string,
  updateMutationRefetchQueries?: InternalRefetchQueriesInclude,
  onWidgetCreate?: (
    widgetType: EnumWidgetType,
    name: string,
    listId: string,
  ) => void,
  onSaveComplete?: () => void,
) => {
  const owner = useLoggedInAs();
  const { t } = useTranslation();
  const { toastFactory } = useToastContext();

  const {
    error: tagsError,
    loading: tagsLoading,
    tags,
  } = useGetAllOrganizationTags();

  // State to hold all the filters that have been applied to this list so far
  const [appliedFilters, setAppliedFilters] = useState<ListFilters>(
    defaultList
      ? getDefaultFiltersFromListSelections(defaultList.selections)
      : defaultFilters,
  );

  // State to hold search string value to help filter operators for MANUAL lists
  const [search, setSearch] = useState("");

  // State to hold the selected operators for MANUAL lists
  const [selectedOperators, setSelectedOperators] = useState<
    OperatorManyQuery["operatorMany"]
  >([]);

  // State to check if we are fetching initial operators for `MANUAL` lists
  // This needs to be set so that we can grab the `selectedOperators`
  // by passing in the `_entityId` filter to `operatorManyQueryVariables`
  // and then to reset it again so that we can search more operators.
  const [
    isFetchingDefaultManualOperators,
    setIsFetchingDefaultManualOperators,
  ] = useState(true);

  const operatorManyQueryVariables: QueryOperatorManyArgs = useMemo(() => {
    const {
      daysOfOperation,
      filledTagGroups,
      mediaQuality,
      operationalStatus,
      pricing,
      seasonality,
      tags: selectedTags,
      visibility,
      digitalHealth,
      location,
    } = appliedFilters;

    const queryArgs: QueryOperatorManyArgs = {
      publicationStatus: visibility.length
        ? visibility === "LIVE"
          ? PublicationStatusType.Approved
          : PublicationStatusType.Unpublished
        : undefined,
      avgImageQuality: mediaQuality.length ? mediaQuality : undefined,
      limit: 10000,
      // If a widget name is provided, we are fetching external operators
      // If no visibility is selected, we are fetching internal operators that can be any status
      useCase:
        widgetName && widgetName.length > 0
          ? OperatorManyUseCaseEnum.External
          : !visibility.length
            ? OperatorManyUseCaseEnum.Internal
            : undefined,
      filter: {
        _entityId:
          listType === EnumListType.Manual &&
          defaultList?.selections.filter._entityId &&
          isFetchingDefaultManualOperators
            ? defaultList?.selections.filter._entityId
            : undefined,
        tags: getTagsTagTypeForQueryArgs(selectedTags, filledTagGroups, tags),
        // Filter the operators by the businessType filledTagGroups ONLY
        // A businessType tagGroup can be set on the operator without selecting
        // businessType tags under that group, therefore we must filter by the
        // operator's selectedTagGroups for the businessType filledTagGroups:
        selectedTagGroups: filledTagGroups.businessType.length
          ? filledTagGroups.businessType
          : undefined,
        pricing: pricing.length ? pricing : undefined,
        operationalStatus: operationalStatus.length
          ? operationalStatus
          : undefined,
        hoursOfOperation:
          daysOfOperation.length > 0
            ? daysOfOperation.reduce((acc, day) => {
                const key = day.toLowerCase() as keyof BooleanHoursOfOperation;
                acc[key] = true; // Mark the day as true
                return acc;
              }, {} as BooleanHoursOfOperation)
            : undefined,
        seasonality:
          seasonality.length > 0
            ? seasonality.reduce((acc, month) => {
                const key = month.toLowerCase() as keyof BooleanSeasonality;
                acc[key] = true; // Mark the month as true
                return acc;
              }, {} as BooleanSeasonality)
            : undefined,
        // Search is only included here to help filter manual lists, this arg
        // will always be undefined when creating a SMART list
        search: search.trim().length > 0 ? search.trim() : undefined,
        // Check to see if longitude and latitude are valid
        location:
          location.longitude &&
          location.latitude &&
          isLongLatValid(location.longitude, location.latitude)
            ? [location.longitude, location.latitude, location.radius]
            : undefined,
      },
    };

    if (digitalHealth.length > 0) {
      digitalHealth.forEach((v) => {
        if (v === "NO_IMAGES") {
          queryArgs["imageCount"] = { _eq: 0 };
        }
        if (v === "NO_WEBSITE") {
          queryArgs["filter"] = {
            ...queryArgs.filter,
            contact: { links: { website: false } },
          };
        }
      });
    }

    return queryArgs;
  }, [
    appliedFilters,
    defaultList?.selections.filter._entityId,
    isFetchingDefaultManualOperators,
    listType,
    search,
    tags,
    widgetName,
  ]);

  const { data, loading, error } = useOperatorManyQuery({
    variables: operatorManyQueryVariables,
    onCompleted: (data) => {
      if (
        listType === EnumListType.Manual &&
        isFetchingDefaultManualOperators &&
        data.operatorMany.length === defaultList?.entities?.length
      ) {
        setSelectedOperators(data.operatorMany);
        setIsFetchingDefaultManualOperators(false);
      }
    },
  });

  const [createList, { loading: listCreateLoading }] = useListCreateMutation();
  const [updateList, { loading: listUpdateLoading }] =
    useListUpdateByIdMutation();

  const hasActiveFiltersApplied =
    !_.isEqual(appliedFilters, defaultFilters) || search.trim().length > 0;

  const handleUpdateAppliedFilters = async (filter: Partial<ListFilters>) => {
    setAppliedFilters((prev) => ({ ...prev, ...filter }));
  };

  const handleResetAppliedFilters = () => {
    setAppliedFilters(
      defaultList
        ? getDefaultFiltersFromListSelections(defaultList.selections)
        : defaultFilters,
    );
  };

  const handleResetSelectedOperators = () => {
    if (listType === EnumListType.Manual) {
      setIsFetchingDefaultManualOperators(true);
    } else {
      setSelectedOperators([]);
    }
  };

  const handleUpdateSearch = (value: string) => {
    // We don't want to trim() here because it doesn't let us search for multiple words.
    // Instead, we'll trim() the search string when it's used in the operatorMany query.
    // We can, however, use trimStart() here to remove leading whitespace.
    setSearch(value.trimStart());
  };

  const handleUpdateSelectedOperators = (
    operator: OperatorManyQuery["operatorMany"][0],
  ) => {
    const isRemoving = !!selectedOperators.find(
      (op) => op._versionId === operator._versionId,
    );

    if (isRemoving) {
      setSelectedOperators((prev) =>
        prev.filter((op) => op._versionId !== operator._versionId),
      );
    } else {
      setSelectedOperators((prev) => [...prev, operator]);
    }
  };

  const handleListSave = async () => {
    try {
      // For SMART lists include all the filters in the selections
      // For MANUAL lists, only include the selected entity ids in the selections and use the internal useCase
      const selections: QueryOperatorManyArgs =
        listType === EnumListType.Smart
          ? operatorManyQueryVariables
          : {
              filter: {
                _entityId: selectedOperators.map((op) => op._entityId),
              },
              useCase: OperatorManyUseCaseEnum.Internal,
            };

      // For SMART lists, use the data from the operatorMany query as the entities
      // For MANUAL lists, use the selectedOperators state as the entities
      const entityReferences =
        listType === EnumListType.Smart
          ? data && data.operatorMany.length > 0
            ? data.operatorMany.map((op) => ({
                _entityId: op._entityId,
                _versionId: op._versionId,
                type: EnumVersionedEntityReferenceType.Operator,
              }))
            : []
          : selectedOperators.map((op) => ({
              _entityId: op._entityId,
              _versionId: op._versionId,
              type: EnumVersionedEntityReferenceType.Operator,
            }));

      // Save `filledTagGroups` to `selections` if list is `SMART`
      // Creates an inferred array of `filledTagGroups` to avoid running complex logic on FE
      // If `location` is filled out, also save the location string to pre-fill list on FE
      // (actual address is not needed on the `operatorMany` query, as we filter by lat, long and radius)
      const recordSelections = {
        ...selections,
        filter: {
          ...selections.filter,
          ...(selections.filter?.location?.length === 3 &&
          appliedFilters.location.address
            ? { locationAddress: appliedFilters.location.address }
            : {}),
          ...(listType === EnumListType.Smart
            ? { filledTagGroups: appliedFilters.filledTagGroups }
            : {}),
        },
      };

      if (!defaultList) {
        await createList({
          variables: {
            record: {
              name: createMultilingual(listName),
              description: createMultilingual(""),
              type: listType,
              selections: recordSelections,
              archived: false,
              owner: {
                type: EnumOwnerType.Organization,
                entityId: owner?.entityId,
              },
              entityReferences,
            },
          },
          refetchQueries: [ListManyDocument],
        }).then((res) => {
          if (
            res.data?.listCreate?.record?._id &&
            onWidgetCreate &&
            widgetName
          ) {
            onWidgetCreate(
              EnumWidgetType.Operator,
              widgetName,
              res.data?.listCreate?.record?._id,
            );
          }
        });
      } else {
        await updateList({
          variables: {
            id: defaultList._id,
            record: {
              selections: recordSelections,
              entityReferences,
            },
          },
          refetchQueries: updateMutationRefetchQueries,
        }).then((res) => {
          setAppliedFilters(
            getDefaultFiltersFromListSelections(
              res.data?.listUpdateById.selections,
            ),
          );
        });
      }
      onSaveComplete?.();
      toastFactory.create({
        title: t("core:SUCCESS"),
        description: t("customLists:LIST.CREATE_NEW.FORM.SUCCESS", {
          listName,
          action: t(`core:FORM_ACTIONS.${defaultList ? "UPDATED" : "CREATED"}`),
        }),
      });
    } catch {
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t("customLists:LIST.CREATE_NEW.FORM.ERROR", {
          listName,
          action: t(`core:FORM_ACTIONS.${defaultList ? "UPDATED" : "CREATED"}`),
        }),
      });
    }
  };

  return {
    appliedFilters,
    hasActiveFiltersApplied,
    search,
    selectedOperators,
    listCreateLoading,
    listUpdateLoading,
    operatorsLoading: loading,
    operatorsError: error,
    operators: data?.operatorMany,
    onUpdateAppliedFilters: handleUpdateAppliedFilters,
    onResetAppliedFilters: handleResetAppliedFilters,
    onResetSelectedOperators: handleResetSelectedOperators,
    onListSave: handleListSave,
    onUpdateSearch: handleUpdateSearch,
    onUpdateSelectedOperators: handleUpdateSelectedOperators,
    allTagsForOrganization: tags,
    tagsLoading,
    tagsError,
  };
};
